import React, { useMemo } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip as MuiTooltip,
  IconButton,
  Tabs,
  Tab,
  TableSortLabel,
  Link,
} from '@material-ui/core';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import SprintItemDrawer from './SprintItemDrawer';
import RecommendTasksModal from './RecommendTasksModal';
import AdminCommentsSection from './AdminCommentsSection';

const humanize = (value) => {
  const s = String(value || '').toLowerCase();
  return s.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const badgeBase = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.4,
};

const statusBadgeStyle = (status) => {
  const s = String(status || '').toLowerCase();
  switch (s) {
    case 'completed':
      return { ...badgeBase, backgroundColor: 'var(--succession-success-bg, rgba(16,185,129,0.1))', color: 'var(--succession-success, #10b981)', border: '1px solid var(--succession-success, #10b981)' };
    case 'in_progress':
      return { ...badgeBase, backgroundColor: 'var(--succession-warning-bg, rgba(245,158,11,0.1))', color: 'var(--succession-warning, #f59e0b)', border: '1px solid var(--succession-warning, #f59e0b)' };
    case 'blocked':
      return { ...badgeBase, backgroundColor: 'var(--succession-error-bg, rgba(239,68,68,0.1))', color: 'var(--succession-error, #ef4444)', border: '1px solid var(--succession-error, #ef4444)' };
    case 'wont_do':
      return { ...badgeBase, backgroundColor: 'var(--succession-bg-secondary, #f8fafc)', color: 'var(--succession-text-secondary, #64748B)', border: '1px solid var(--succession-border, #CBD5E1)' };
    case 'open':
    default:
      return { ...badgeBase, backgroundColor: 'var(--succession-primary-10, rgba(84,137,182,0.1))', color: 'var(--succession-primary, #5489B6)', border: '1px solid var(--succession-primary, #5489B6)' };
  }
};

const categoryBadgeStyle = (category) => {
  const c = String(category || '').toLowerCase();
  switch (c) {
    case 'identify':
      return { ...badgeBase, backgroundColor: 'var(--succession-primary-10, rgba(84,137,182,0.1))', color: 'var(--succession-primary, #5489B6)', border: '1px solid var(--succession-primary, #5489B6)' };
    case 'protect':
      return { ...badgeBase, backgroundColor: 'var(--succession-warning-bg, rgba(245,158,11,0.1))', color: 'var(--succession-warning, #f59e0b)', border: '1px solid var(--succession-warning, #f59e0b)' };
    case 'build':
      return { ...badgeBase, backgroundColor: 'var(--succession-success-bg, rgba(16,185,129,0.1))', color: 'var(--succession-success, #10b981)', border: '1px solid var(--succession-success, #10b981)' };
    case 'harvest':
      return { ...badgeBase, backgroundColor: 'var(--succession-error-bg, rgba(239,68,68,0.1))', color: 'var(--succession-error, #ef4444)', border: '1px solid var(--succession-error, #ef4444)' };
    case 'manage':
    default:
      return { ...badgeBase, backgroundColor: 'var(--succession-bg-secondary, #f8fafc)', color: 'var(--succession-text-secondary, #64748B)', border: '1px solid var(--succession-border, #CBD5E1)' };
  }
};

const GET_ADMIN_SPRINT = gql`
  query AdminSprint($id: ID!) {
    adminSprint(id: $id) {
      id
      name
      status
      start_on
      end_on
      progress_percent_cached
      user { id allowed_name inventory_user { id } }
      items {
        id
        title
        status
        dueAt
        cepaCategory
        taskTemplate { key domain cepaCategory deadlineInDays }
        documents {
          id
          name
          download_url
          created_at
        }
      }
      events { id event_type occurred_at }
      comment_count
      unread_comment_count
    }
  }
`;

const UPDATE_ACTION_PLAN_ITEM = gql`
  mutation UpdateActionPlanItem($id: ID!, $status: ActionPlanItemStatusEnum) {
    update_action_plan_item(id: $id, status: $status) {
      id
      status
      dueAt
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(8),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statCard: {
    textAlign: 'center',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
}));

const SprintDetail = ({ match, history }) => {
  const classes = useStyles();
  const id = match?.params?.id;
  const { data, loading, error, refetch } = useQuery(GET_ADMIN_SPRINT, { variables: { id } });
  const [updateItem] = useMutation(UPDATE_ACTION_PLAN_ITEM, { onCompleted: () => refetch() });
  const [selectedItem, setSelectedItem] = React.useState(null);

  const s = data?.adminSprint;
  const items = (s && s.items) || [];
  const norm = (v) => String(v || '').toLowerCase();
  const [tab, setTab] = React.useState('items');
  const [recommendOpen, setRecommendOpen] = React.useState(false);
  const [orderBy, setOrderBy] = React.useState('title');
  const [order, setOrder] = React.useState('asc');
  const stats = useMemo(() => {
    const total = items.length;
    const completed = items.filter(i => norm(i.status) === 'completed').length;
    const blocked = items.filter(i => norm(i.status) === 'blocked').length;
    const progress = Number((s && s.progress_percent_cached) || 0).toFixed(2);
    return { total, completed, blocked, progress };
  }, [items, s]);

  const statusData = useMemo(() => ([
    { name: 'OPEN', value: items.filter(i => norm(i.status) === 'open').length },
    { name: 'IN_PROGRESS', value: items.filter(i => norm(i.status) === 'in_progress').length },
    { name: 'COMPLETED', value: items.filter(i => norm(i.status) === 'completed').length },
    { name: 'BLOCKED', value: items.filter(i => norm(i.status) === 'blocked').length },
    { name: 'WONT_DO', value: items.filter(i => norm(i.status) === 'wont_do').length },
  ]), [items]);

  const categoryData = useMemo(() => {
    const groups = new Map();
    items.forEach((i) => {
      const cat = (i.cepaCategory || 'unknown').toString();
      const g = groups.get(cat) || { name: cat, total: 0, completed: 0, percent: 0 };
      g.total += 1;
      if (norm(i.status) === 'completed') g.completed += 1;
      groups.set(cat, g);
    });
    return Array.from(groups.values()).map(g => ({ ...g, percent: g.total ? (g.completed * 100.0 / g.total) : 0 }));
  }, [items]);

  if (loading) return <Box p={3}><Typography>Loading…</Typography></Box>;
  if (error) return <Box p={3}><Typography color="error">Error loading sprint</Typography></Box>;
  if (!s) return <Box p={3}><Typography>Not found</Typography></Box>;

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4">Sprint: {s.name || s.id}</Typography>
        <Box>
          <Button variant="outlined" onClick={() => (history ? history.push('/admin/sprints') : (window.location.href = '/admin/sprints'))}>
            Back to Sprints
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper>
            <Box p={2}>
              <Typography variant="h6">Overview</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle1" color="textSecondary">User</Typography>
                  <Typography variant="h6">
                    {s.user?.inventory_user?.id ? (
                      <Link href={`/admin/inventory_users?inv_user=${s.user.inventory_user.id}`}>{s.user?.allowed_name}</Link>
                    ) : (
                      s.user?.allowed_name
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle1" color="textSecondary">Status</Typography>
                  <Typography variant="h6">{s.status}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle1" color="textSecondary">Start</Typography>
                  <Typography variant="h6">{s.start_on || '-'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle1" color="textSecondary">End</Typography>
                  <Typography variant="h6">{s.end_on || '-'}</Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent className={classes.statCard}>
                  <Typography variant="h4">{stats.progress}%</Typography>
                  <Typography color="textSecondary">Progress</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent className={classes.statCard}>
                  <Typography variant="h4">{stats.completed}/{stats.total}</Typography>
                  <Typography color="textSecondary">Completed</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent className={classes.statCard}>
                  <Typography variant="h4">{stats.blocked}</Typography>
                  <Typography color="textSecondary">Blocked</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Box mt={3}>
        <Paper>
          <Box px={2}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary">
              <Tab value="items" label="Items" />
              <Tab value="charts" label="Charts" />
            </Tabs>
          </Box>

          {tab === 'charts' && (
            <>
              <Box px={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography color="textSecondary">Status Breakdown</Typography>
                  <MuiTooltip title="Count of sprint items by status (open, in progress, completed, blocked, won't do).">
                    <IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton>
                  </MuiTooltip>
                </Box>
                <Box height={220}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={statusData}>
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#3f51b5">
                        <LabelList dataKey="value" position="top" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
              <Box px={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
                  <Typography color="textSecondary">Completion by Category</Typography>
                  <MuiTooltip title="For items in this sprint, completion rate by CEPA category.">
                    <IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton>
                  </MuiTooltip>
                </Box>
                <Box height={220}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={categoryData}>
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, 100]} tickFormatter={(v)=>`${v}%`} />
                      <Tooltip formatter={(v)=>`${Number(v).toFixed(1)}%`} />
                      <Bar dataKey="percent" name="Completed %" fill="#4caf50">
                        <LabelList dataKey="percent" position="top" formatter={(v)=>`${Number(v).toFixed(0)}%`} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </>
          )}

          {tab === 'items' && (
            <>
              <Box p={2} display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">Items</Typography>
                <Button variant="contained" color="primary" onClick={()=>setRecommendOpen(true)}>Recommend Task</Button>
              </Box>
              <Box className={classes.tableContainer}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={orderBy==='title'?order:false}>
                        <TableSortLabel active={orderBy==='title'} direction={orderBy==='title'?order:'asc'} onClick={() => { setOrderBy('title'); setOrder(order==='asc'?'desc':'asc'); }}>
                          Title
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy==='status'?order:false}>
                        <TableSortLabel active={orderBy==='status'} direction={orderBy==='status'?order:'asc'} onClick={() => { setOrderBy('status'); setOrder(order==='asc'?'desc':'asc'); }}>
                          Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy==='dueAt'?order:false}>
                        <TableSortLabel active={orderBy==='dueAt'} direction={orderBy==='dueAt'?order:'asc'} onClick={() => { setOrderBy('dueAt'); setOrder(order==='asc'?'desc':'asc'); }}>
                          Due
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy==='cepaCategory'?order:false}>
                        <TableSortLabel active={orderBy==='cepaCategory'} direction={orderBy==='cepaCategory'?order:'asc'} onClick={() => { setOrderBy('cepaCategory'); setOrder(order==='asc'?'desc':'asc'); }}>
                          Category
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items
                      .slice()
                      .sort((a,b) => {
                        const av = orderBy==='status' ? norm(a.status) : orderBy==='dueAt' ? (a.dueAt? new Date(a.dueAt).getTime(): 0) : orderBy==='cepaCategory' ? String(a.cepaCategory||'').toLowerCase() : String(a.title||'').toLowerCase();
                        const bv = orderBy==='status' ? norm(b.status) : orderBy==='dueAt' ? (b.dueAt? new Date(b.dueAt).getTime(): 0) : orderBy==='cepaCategory' ? String(b.cepaCategory||'').toLowerCase() : String(b.title||'').toLowerCase();
                        if (av === bv) return 0;
                        const cmp = av > bv ? 1 : -1;
                        return order==='asc' ? cmp : -cmp;
                      })
                      .map((i) => (
                      <TableRow key={i.id} hover style={{ cursor: 'pointer' }} onClick={() => setSelectedItem(i)}>
                        <TableCell>{i.title}</TableCell>
                        <TableCell>
                          <span style={statusBadgeStyle(i.status)}>{humanize(i.status)}</span>
                        </TableCell>
                        <TableCell>{i.dueAt || '-'}</TableCell>
                        <TableCell>
                          <span style={categoryBadgeStyle(i.cepaCategory)}>{humanize(i.cepaCategory)}</span>
                        </TableCell>
                        <TableCell>
                          <FormControl variant="outlined" size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                              label="Status"
                              value={i.status}
                              onChange={(e) => updateItem({ variables: { id: i.id, status: e.target.value } })}
                              style={{ minWidth: 160 }}
                            >
                              <MenuItem value="OPEN">OPEN</MenuItem>
                              <MenuItem value="IN_PROGRESS">IN_PROGRESS</MenuItem>
                              <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                              <MenuItem value="BLOCKED">BLOCKED</MenuItem>
                              <MenuItem value="WONT_DO">WONT_DO</MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            variant="text"
                            color="primary"
                            component="a"
                            href={`/admin/action_plan_items/${i.id}/education.pdf`}
                            style={{ marginLeft: 12 }}
                          >
                            Download PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </Paper>
      </Box>

      <Box mt={3}>
        <AdminCommentsSection
          commentableId={s?.id}
          commentableType="Sprint"
          title="Coach's Corner - Sprint Discussion"
          maxHeight={360}
        />
      </Box>

      <RecommendTasksModal
        open={recommendOpen}
        onClose={()=>setRecommendOpen(false)}
        sprintId={s.id}
        onCompleted={()=>refetch()}
      />

      <Box mt={3}>
        <Paper>
          <Box p={2}>
            <Typography variant="h6">Events</Typography>
          </Box>
          <Box p={2}>
            {(s.events || []).map((e) => (
              <Box key={e.id} display="flex" justifyContent="space-between" py={1} style={{ borderBottom: '1px solid #eee' }}>
                <Typography color="textSecondary">{e.occurred_at}</Typography>
                <Typography>{e.event_type}</Typography>
              </Box>
            ))}
            {(!s.events || s.events.length === 0) && (
              <Typography color="textSecondary">No events yet.</Typography>
            )}
          </Box>
        </Paper>
      </Box>
      <SprintItemDrawer
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        task={selectedItem}
        onSave={async (taskId, updates) => {
          const variables = { id: taskId };
          if (updates.status) variables.status = updates.status.toUpperCase();
          if (updates.dueAt !== undefined) variables.due_at = updates.dueAt;
          if (updates.title !== undefined) variables.title = updates.title;
          if (updates.description !== undefined) variables.description = updates.description;
          await updateItem({ variables });
        }}
        onMarkCompleted={async (taskId) => {
          await updateItem({ variables: { id: taskId, status: 'COMPLETED' } });
          setSelectedItem(null);
        }}
        onTaskUpdated={async () => {
          const result = await refetch();
          // Update selectedItem with fresh data after refetch
          if (selectedItem && result.data?.adminSprint?.items) {
            const updatedTask = result.data.adminSprint.items.find((item) => item.id === selectedItem.id);
            if (updatedTask) {
              setSelectedItem(updatedTask);
            }
          }
        }}
      />
    </Box>
  );
};

export default SprintDetail;


